import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateDb, formatDateHourDb } from "../utils/tools";
import {
  customDateFormatType,
  customDateFormatType2,
  formatDateForInput,
  formatNumber,
} from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.bookingManagement;
const queryKey = "BookingManagement";

async function fetchBookingManagement(
  currentPage,
  search,
  projectId,
  bookerId,
  bookingEventId,
  status,
  isLocking,
  customerTypeId,
  customerId,
  startTime,
  endTime,
  saleBlockId,
  saleAreaId,
  startDateBooking,
  endDateBooking,
  apartmentId
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (search !== undefined) options.search = search;
    if (projectId?.length) options.projectId = projectId;
    if (bookerId) options.bookerId = bookerId;
    if (bookingEventId) options.bookingEventId = bookingEventId;
    if (status?.length) options.status = status;
    if (isLocking) options.isLocking = isLocking;
    if (customerTypeId) options.customerTypeId = customerTypeId;
    if (customerId) options.customerId = customerId;
    if (startTime) options.startTime = customDateFormatType(startTime);
    if (endTime) options.endTime = customDateFormatType(endTime);
    if (saleBlockId) options.saleBlockId = saleBlockId;
    if (saleAreaId) options.saleAreaId = saleAreaId;
    if (apartmentId?.length) options.apartmentId = apartmentId;
    if (startDateBooking)
      options.startTimeBooking = customDateFormatType(startDateBooking);
    if (endDateBooking)
      options.endTimeBooking = customDateFormatType(endDateBooking);

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI("get", `/booking?${queryParams}`);
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "",
        status: item?.status || "",
        // statusRefund: item?.status || "",
        statusLocking: item?.locking?._id ? true : false,
        // customerType: item?.customer?.customerType?.name || "-",
        nameCustomer: item?.customer?.name || "-",
        codeApartment: item?.apartment?.code || "-",
        nameProject: item?.project?.name || "-",
        bookingTime: item?.bookingTime ? formatDateDb(item?.bookingTime) : "-",
        bookingEvent: item?.bookingEvent?.name || "-",
        price: item?.price ? `${formatNumber(item?.price)} VNĐ` : "0 VNĐ",
        nameBooker: item?.bookerObject?.fullName || "-",
        saleBlock: item?.saleBlock?.name || "-",
        saleArea: item?.saleArea?.name || "-",
        createdAt: item?.createdAt ? formatDateHourDb(item?.createdAt) : "-",
        // agentName: item?.agent?.name || "-",
        // note: item?.note || "-",
        edit: {
          id: item?._id || "",
          note: item?.note || "",
          nameBooker: item?.bookerObject?.fullName || "",
          projectId: item?.projectId || "",
          bookingEventId: item?.bookingEventId || "",
          customerId: item?.customerId || "",
          createdAt: item?.createdAt ? formatDateDb(item?.createdAt) : "",
          price: item?.price || "",
          bookingTime: item?.bookingTime
            ? formatDateForInput(item?.bookingTime)
            : "",
          apartmentId: item?.apartmentId || "",
          bookerId: item?.bookerId || "",
          images: item?.images || [],
        },
        detail: {
          id: item?._id || "",
          bookingEvent: item?.bookingEvent?.name || "",
          status: item?.status || "",
          note: item?.note || "",
          nameBooker: item?.bookerObject?.fullName || "",
          bookerId: item?.bookerId || "",
          createdAt: item?.createdAt || "",
          projectId: item?.projectId || "",
          projectName: item?.project?.name || "",
          customerName: item?.customer?.name || "",
          customerId: item?.customerId || "",
          customer: item?.customer,
          apartmentName: item?.apartment?.name || "",
          apartmentId: item?.apartmentId || "",
          apartmentCode: item?.apartment?.code || "",
          price: item?.price || "",
          bookingTime: item?.bookingTime || "",
          bookingEventId: item?.bookingEventId || "",
          images: item?.images || [],
          agentName: item?.agent?.name || "-",
          saleBlock: item?.saleBlock?.name || "-",
          saleArea: item?.saleArea?.name || "-",
        },
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useBookingManagement(
  search,
  projectId,
  bookerId,
  bookingEventId,
  status,
  isLocking,
  customerTypeId,
  customerId,
  startDate,
  endDate,
  saleBlockId,
  saleAreaId,
  startDateBooking,
  endDateBooking,
  apartmentId
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      search,
      projectId,
      bookerId,
      bookingEventId,
      status,
      isLocking,
      customerTypeId,
      customerId,
      startDate,
      endDate,
      saleBlockId,
      saleAreaId,
      startDateBooking,
      endDateBooking,
      apartmentId,
    ],
    queryFn: () =>
      fetchBookingManagement(
        currentPage,
        search,
        projectId,
        bookerId,
        bookingEventId,
        status,
        isLocking,
        customerTypeId,
        customerId,
        startDate,
        endDate,
        saleBlockId,
        saleAreaId,
        startDateBooking,
        endDateBooking,
        apartmentId
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    bookingManagementData: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
